import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { getNotificationSettings, updateNotificationSettings } from '../api';

export default function NotificationSettings() {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getNotificationSettings();
      setPreferences(res.data.preferences || []);
    } catch (err) {
      setError('Failed to load notification settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (type) => {
    setPreferences(prev =>
      prev.map(p => p.type === type ? { ...p, enabled: !p.enabled } : p)
    );
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateNotificationSettings({
        preferences: preferences.map(p => ({ type: p.type, enabled: p.enabled }))
      });
      setSuccess('Notification settings saved successfully');
    } catch (err) {
      setError('Failed to save notification settings');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Notification Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control which email notifications are sent for your organization. Disabled notifications will be silently skipped.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <List disablePadding>
          {preferences.map((pref, index) => (
            <React.Fragment key={pref.type}>
              {index > 0 && <Divider />}
              <ListItem sx={{ py: 2, px: 3 }}>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="medium">
                      {pref.label}
                    </Typography>
                  }
                  secondary={pref.description}
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={pref.enabled}
                    onChange={() => handleToggle(pref.type)}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </Box>
  );
}
